import React from 'react'
import { type FormContextValues } from 'react-hook-form'
import { type OptionProps } from 'react-select'
import { Select } from '@retailer-platform/shared-components'
import {
  type SelectProps,
  type SimpleOption,
} from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import { type Styles } from 'react-select'
import { colors } from 'gin-and-tonic/foundation/colors'
import { fontSize } from 'gin-and-tonic/foundation/fontSize'
import { getRepeatTypeFromDaysOfWeek } from 'domains/shifts/components/utils/shiftsUtils'
import { useShiftDetailsFormStyles } from 'domains/shifts/components/contexts/ShiftDetailsFormStylesContext'
import { usePartnerPickRecurringShiftFormOptions } from './usePartnerPickRecurringShiftFormOptions'
import {
  type DayOfWeekOption,
  type PartnerPickRecurringShiftsFormData,
} from './recurringShift.types'
import {
  FormRow,
  StyledCheckbox,
  StyledCheckboxContainer,
  StyledDropdownContainer,
  StyledLabel,
} from './ShiftDetailsForm.styles'

interface SimpleOptionProps
  extends Omit<SelectProps<SimpleOption<DayOfWeekOption>>, 'options' | 'isMulti'> {}

interface Props extends SimpleOptionProps {
  setValue: FormContextValues<PartnerPickRecurringShiftsFormData>['setValue']
  control: FormContextValues<PartnerPickRecurringShiftsFormData>['control']
  getValues: FormContextValues<PartnerPickRecurringShiftsFormData>['getValues']
  register: FormContextValues<PartnerPickRecurringShiftsFormData>['register']
  watch: FormContextValues<PartnerPickRecurringShiftsFormData>['watch']
  label: string
  date: string
}

export const CustomDaysDropdown: React.FunctionComponent<Props> = ({
  label,
  setValue,
  watch,
  date,
}) => {
  const { daysOfWeekOptions } = usePartnerPickRecurringShiftFormOptions(date)
  const { daysOfWeek } = watch(['daysOfWeek', 'repeatShift'])

  const onChange = (data: DayOfWeekOption[]) => {
    const repeatType = getRepeatTypeFromDaysOfWeek(data)
    setValue('repeatType', repeatType, true)
    setValue('daysOfWeek', data, true)
  }

  const Option: React.FunctionComponent<OptionProps<DayOfWeekOption>> = props => (
    <StyledCheckboxContainer {...props.innerProps}>
      <StyledLabel>
        <StyledCheckbox type="checkbox" checked={props.isSelected} />
        {props.label}
      </StyledLabel>
    </StyledCheckboxContainer>
  )

  // convert full weekday strings to their abbreviations
  const abbreviatedWeekdays = daysOfWeek.map(({ label }) => label.slice(0, 3)).join(', ')

  const selectStyles: Styles = {
    placeholder: () => ({
      color: colors.GRAYSCALE.X70,
      fontSize: fontSize.X15,
      fontWeight: 600,
      lineHeight: '22px',
    }),
  }
  const styles = useShiftDetailsFormStyles(selectStyles)

  return (
    <FormRow>
      <StyledDropdownContainer>
        <StyledLabel>{label}</StyledLabel>
        <Select
          isMulti
          onChange={onChange}
          components={{ Option }}
          options={daysOfWeekOptions}
          value={daysOfWeek}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          backspaceRemovesValues={false}
          controlShouldRenderValue={false}
          placeholder={
            <span data-testid="shopper-shifts-customday-placeholder">{abbreviatedWeekdays}</span>
          }
          styles={styles}
        />
      </StyledDropdownContainer>
    </FormRow>
  )
}
